/**
 * useHIITTimer - Minuteur intelligent HIIT
 * Gère le timer travail/repos avec sons, vibrations, TTS
 */

import { useState, useRef, useCallback, useEffect } from 'react';

const ENCOURAGEMENTS = [
  'Tu gères ! Continue comme ça !',
  'Bravo ! Encore un cycle de fait !',
  'Impressionnant ! Tu es une machine !',
  'Chaque seconde compte, et tu les maîtrises !',
  "Rien ne peut t'arrêter !",
  'Le plus dur est derrière toi !',
  'Ta détermination est inspirante !',
  'Force mentale : 100% !',
  'Tu repousses tes limites !',
  'Champion(ne) en devenir !',
];

export function useHIITTimer({ exercises = [], workTime = 30, restTime = 20, onComplete }) {
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [currentCycle, setCurrentCycle] = useState(0);
  const [phase, setPhase] = useState('ready'); // ready, work, rest, complete
  const [timeLeft, setTimeLeft] = useState(workTime);
  const [totalElapsed, setTotalElapsed] = useState(0);
  const [encouragement, setEncouragement] = useState('');
  const [milestoneReached, setMilestoneReached] = useState(false);
  const [completedCycles, setCompletedCycles] = useState(0);

  const intervalRef = useRef(null);
  const backgroundRef = useRef(null); // timestamp when app went to background
  const totalCycles = exercises.length;

  // Page Visibility: handle background/foreground transitions
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && isRunning && !isPaused) {
        // App went to background — record timestamp
        backgroundRef.current = Date.now();

        // Send push notification so user knows timer is still running
        if ('Notification' in window && Notification.permission === 'granted') {
          try {
            new Notification('Go Sport — Séance en cours', {
              body: `${phase === 'work' ? '🔴 Travail' : '🔵 Repos'} · ${timeLeft}s restantes`,
              icon: '/favicon.ico',
              tag: 'gosport-live',
            });
          } catch { /* ignore */ }
        }
      } else if (!document.hidden && backgroundRef.current && isRunning) {
        // App came back to foreground — fast-forward elapsed time
        const elapsed = Math.floor((Date.now() - backgroundRef.current) / 1000);
        backgroundRef.current = null;

        if (elapsed > 0 && elapsed < 1800) {
          // Compensate by reducing timeLeft (max 30 min)
          setTimeLeft(prev => Math.max(0, prev - elapsed));
          setTotalElapsed(prev => prev + elapsed);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isRunning, isPaused, phase, timeLeft]);

  // Text-to-Speech
  const speak = useCallback((text) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'fr-FR';
      utterance.rate = 1.1;
      utterance.volume = 0.8;
      window.speechSynthesis.speak(utterance);
    }
  }, []);

  // Son bip
  const playBeep = useCallback((count = 1, freq = 800) => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      for (let i = 0; i < count; i++) {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = freq;
        gain.gain.value = 0.3;
        osc.start(ctx.currentTime + i * 0.4);
        osc.stop(ctx.currentTime + i * 0.4 + 0.15);
      }
    } catch (e) {
      // Silently fail if audio context unavailable
    }
  }, []);

  // Vibration
  const vibrate = useCallback((pattern = [200]) => {
    if ('vibrate' in navigator) {
      navigator.vibrate(pattern);
    }
  }, []);

  // Cleanup
  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  // Timer tick
  useEffect(() => {
    if (!isRunning || isPaused || phase === 'ready' || phase === 'complete') {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }

    intervalRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          if (phase === 'work') {
            vibrate([200, 100, 200]);
            playBeep(1, 600);
            setCompletedCycles(c => c + 1);

            const newCompleted = completedCycles + 1;

            setEncouragement(ENCOURAGEMENTS[Math.floor(Math.random() * ENCOURAGEMENTS.length)]);
            setTimeout(() => setEncouragement(''), 3000);

            if (newCompleted === Math.ceil(totalCycles / 2) && !milestoneReached) {
              setMilestoneReached(true);
            }

            if (currentCycle >= totalCycles - 1) {
              setPhase('complete');
              setIsRunning(false);
              vibrate([200, 100, 200, 100, 400]);
              playBeep(3, 1000);
              speak('Séance terminée ! Félicitations !');
              onComplete?.({ completedCycles: newCompleted, totalElapsed });
              return 0;
            }

            setPhase('rest');
            const nextEx = exercises[currentCycle + 1];
            if (nextEx) {
              speak(`Repos. Prochain exercice : ${nextEx.name}`);
            }
            return restTime;
          } else {
            vibrate([300]);
            playBeep(2, 800);
            setCurrentCycle(c => c + 1);
            setPhase('work');
            const nextEx = exercises[currentCycle + 1];
            if (nextEx) {
              speak(nextEx.name);
            }
            return workTime;
          }
        }

        if (prev <= 4 && prev > 1) {
          playBeep(1, prev === 2 ? 1000 : 700);
        }

        return prev - 1;
      });

      setTotalElapsed(t => t + 1);
    }, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isRunning, isPaused, phase, currentCycle, totalCycles, workTime, restTime,
    completedCycles, milestoneReached, totalElapsed, exercises, onComplete,
    speak, playBeep, vibrate]);

  const start = useCallback(() => {
    setPhase('work');
    setTimeLeft(workTime);
    setCurrentCycle(0);
    setTotalElapsed(0);
    setCompletedCycles(0);
    setMilestoneReached(false);
    setIsRunning(true);
    setIsPaused(false);

    vibrate([300]);
    playBeep(2, 800);
    if (exercises[0]) {
      speak(`C'est parti ! ${exercises[0].name}`);
    }
  }, [workTime, exercises, speak, playBeep, vibrate]);

  const pause = useCallback(() => {
    setIsPaused(true);
  }, []);

  const resume = useCallback(() => {
    setIsPaused(false);
  }, []);

  const stop = useCallback(() => {
    setIsRunning(false);
    setIsPaused(false);
    setPhase('ready');
    setTimeLeft(workTime);
    setCurrentCycle(0);
    setTotalElapsed(0);
    if (intervalRef.current) clearInterval(intervalRef.current);
  }, [workTime]);

  const skipExercise = useCallback(() => {
    if (phase === 'work') {
      setPhase('rest');
      setTimeLeft(restTime);
      setCompletedCycles(c => c + 1);
    } else if (phase === 'rest') {
      setCurrentCycle(c => c + 1);
      setPhase('work');
      setTimeLeft(workTime);
    }
  }, [phase, restTime, workTime]);

  const prevExercise = useCallback(() => {
    if (currentCycle > 0) {
      setCurrentCycle(c => c - 1);
      setPhase('work');
      setTimeLeft(workTime);
    }
  }, [currentCycle, workTime]);

  const currentExercise = exercises[currentCycle] || null;
  const nextExercise = exercises[currentCycle + 1] || null;
  const progress = totalCycles > 0 ? (completedCycles / totalCycles) * 100 : 0;

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  return {
    isRunning, isPaused, phase, currentCycle, totalCycles, timeLeft,
    totalElapsed, currentExercise, nextExercise, encouragement,
    milestoneReached, completedCycles, progress,
    start, pause, resume, stop, skipExercise, prevExercise, formatTime,
  };
}
